import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import CopyrightIcon from '@mui/icons-material/Copyright';
import CloseIcon from '@mui/icons-material/Close';
import '../shared.css';

const UserDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, userId, logout, permissions, token } = useAuth();

  const hasEdit   = permissions.includes('Edit Administration');

  const [isSavingRoles,  setIsSavingRoles]  = useState(false);
  const [isSavingClaims, setIsSavingClaims] = useState(false);
  const [selectedOption, setSelectedOption] = useState({ value: '1', label: 'Master' });
  const [isEditMode,      setIsEditMode]      = useState(false);
  const [isClaimEditMode, setIsClaimEditMode] = useState(false);
  const [successMessage, setSuccessMessage]  = useState('');
  const [errorMessage,   setErrorMessage]    = useState('');

  const [formData, setFormData] = useState({
    FirstName: '', LastName: '', UserName: '', Email: '', ContactNumber: '', Roles: [],
  });
  const [formDataClaim, setFormDataClaim] = useState({
    FirstName: '', LastName: '', UserName: '', Email: '', ContactNumber: '', Claim: [],
  });

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
  }, [isAuthenticated, companyId, token, logout]);

  useEffect(() => {
    fetchData('UserApi', `/UserEdit/${id}`, token)
      .then(response => {
        if (response.responseCode === '1111') {
          const { User } = response.response_data;
          setFormData(prev => ({
            ...prev, ...User,
            Roles: response.response_data.Roles
              ? response.response_data.Roles.map(r => ({ ...r })) : [],
          }));
          setFormDataClaim(prev => ({
            ...prev, ...User,
            Claim: response.response_data.Claims
              ? response.response_data.Claims.map(c => ({ ...c })) : [],
          }));
        }
      })
      .catch(err => console.error('Error fetching User Details:', err));
  }, [id]);

  const handleChangeRole = (index) => {
    const updatedRoles = formData.Roles.map((role, i) => ({
      ...role, IsSelected: i === index ? !role.IsSelected : false,
    }));
    setFormData(prev => ({ ...prev, Roles: updatedRoles }));
  };

  const getClaimRange = () => {
    const startIndex = (parseInt(selectedOption.value, 10) - 1) * 5;
    return { startIndex, endIndex: startIndex + 4 };
  };

  const handleClaimChange = (index) => {
    const updatedClaims = [...formDataClaim.Claim];
    updatedClaims[index].IsSelected = !updatedClaims[index].IsSelected;
    setFormDataClaim(prev => ({ ...prev, Claim: updatedClaims }));
  };

  const saveRoles = async () => {
    try {
      setIsSavingRoles(true);
      const response = await updateData('UserApi', `SaveUserRoles/tmp/${id}`, formData, token);
      if (response.responseCode === '1111') setSuccessMessage('Roles saved successfully');
      setIsEditMode(false);
      history.push(`/userDetail/${id}`);
    } catch (err) {
      console.error('Error saving roles:', err);
    } finally {
      setIsSavingRoles(false);
    }
  };

  const saveClaims = async () => {
    try {
      setIsSavingClaims(true);
      const response = await updateData('UserApi', `SaveUserClaims/UpdateClaim/tmp/${id}`, formDataClaim, token);
      if (response.responseCode === '1111') setSuccessMessage('Claims saved successfully');
      setIsClaimEditMode(false);
      history.push(`/userDetail/${id}`);
    } catch (err) {
      console.error('Error saving claims:', err);
    } finally {
      setIsSavingClaims(false);
    }
  };

  return (
    <div className="pg-container">
      {/* ── Header ── */}
      <div className="pg-header">
        <h5 className="pg-title">User — Detail</h5>
        <div className="pg-action-row">
          <Link to="/UserIndex" className="pg-btn pg-btn-danger">
            <CloseIcon /> Back to List
          </Link>
        </div>
      </div>

      {errorMessage   && <div className="pg-alert pg-alert-error">{errorMessage}</div>}
      {successMessage && <div className="pg-alert pg-alert-success">{successMessage}</div>}

      {/* ── User Info ── */}
      <div className="pg-card">
        <div className="pg-form-grid pg-form-grid-4">
          <div className="pg-field">
            <label className="pg-label">First Name</label>
            <input className="pg-input" value={formData.FirstName} readOnly />
          </div>
          <div className="pg-field">
            <label className="pg-label">Last Name</label>
            <input className="pg-input" value={formData.LastName} readOnly />
          </div>
          <div className="pg-field">
            <label className="pg-label">User Name</label>
            <input className="pg-input" value={formData.UserName} readOnly />
          </div>
          <div className="pg-field">
            <label className="pg-label">Contact Number</label>
            <input className="pg-input" value={formData.ContactNumber} readOnly />
          </div>
          <div className="pg-field">
            <label className="pg-label">Email</label>
            <input className="pg-input" value={formData.Email} readOnly />
          </div>
        </div>
      </div>

      {/* ── Claims & Roles ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>

        {/* User Claims */}
        <div className="pg-card" style={{ flex: '1 1 300px', minWidth: 260 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#1c2a3a' }}>User Claims</span>
            {hasEdit && (
              !isClaimEditMode
                ? <button className="pg-btn pg-btn-outline" onClick={() => setIsClaimEditMode(true)}>Manage Claims</button>
                : <button className="pg-btn pg-btn-success" onClick={saveClaims} disabled={isSavingClaims}>
                    {isSavingClaims ? 'Saving…' : 'Save Claims'}
                  </button>
            )}
          </div>

          <div style={{ marginBottom: 8 }}>
            <Select
              options={[
                { value: '1', label: 'Master' },
                { value: '2', label: 'Transaction' },
                { value: '3', label: 'Reports' },
                { value: '4', label: 'Administration' },
              ]}
              value={selectedOption}
              onChange={setSelectedOption}
            />
          </div>

          {formDataClaim.Claim && formDataClaim.Claim.length > 0
            ? formDataClaim.Claim.map((claim, index) => {
                const { startIndex, endIndex } = getClaimRange();
                if (index < startIndex || index > endIndex) return null;
                return (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(200,212,224,0.3)' }}>
                    <label htmlFor={`claim-${index}`} style={{ fontSize: 12.5, color: '#374151', cursor: 'pointer' }}>
                      {claim.ClaimValue}
                    </label>
                    <input
                      type="checkbox"
                      id={`claim-${index}`}
                      checked={claim.IsSelected}
                      onChange={() => handleClaimChange(index)}
                      disabled={!isClaimEditMode}
                      style={{ width: 14, height: 14, accentColor: '#c9a96e', cursor: isClaimEditMode ? 'pointer' : 'default' }}
                    />
                  </div>
                );
              })
            : <p style={{ fontSize: 12, color: '#94a3b8' }}>No claims available.</p>
          }
        </div>

        {/* User Roles */}
        <div className="pg-card" style={{ flex: '1 1 300px', minWidth: 260 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#1c2a3a' }}>User Role</span>
            {hasEdit && (
              !isEditMode
                ? <button className="pg-btn pg-btn-outline" onClick={() => setIsEditMode(true)}>Manage Roles</button>
                : <button className="pg-btn pg-btn-success" onClick={saveRoles} disabled={isSavingRoles}>
                    {isSavingRoles ? 'Saving…' : 'Save Roles'}
                  </button>
            )}
          </div>

          {formData.Roles && formData.Roles.length > 0
            ? formData.Roles.map((role, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(200,212,224,0.3)' }}>
                  <label htmlFor={`role-${index}`} style={{ fontSize: 12.5, color: '#374151', cursor: 'pointer' }}>
                    {role.RoleName}
                  </label>
                  <input
                    type="checkbox"
                    id={`role-${index}`}
                    checked={role.IsSelected}
                    onChange={() => handleChangeRole(index)}
                    disabled={!isEditMode}
                    style={{ width: 14, height: 14, accentColor: '#c9a96e', cursor: isEditMode ? 'pointer' : 'default' }}
                  />
                </div>
              ))
            : <p style={{ fontSize: 12, color: '#94a3b8' }}>No roles available.</p>
          }
        </div>

      </div>

      <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
    </div>
  );
};

export default UserDetail;
